import { color, Common } from '@stockbit/ui';
import { AVATAR_URL } from 'constants/api';
import Image from 'global/Image';
import { useEffect, useState } from 'react';
import ERROR_MESSAGE from 'constants/errorMessage';
import { handleErrorAvatar } from 'utils/general';
import alertMessage from 'global/AlertMessage';
import useDropFile from './useDropFile';
import Textarea from './Textarea';
import FileContainer from '../../StreamCompose/FileContainer';
import NestedReplyBox from '../ItemConversation/NestedReplyBox';
import Footer from './Footer';
import ImageItem from './ImageItem';
import { ComposeContent, ReplyCompose } from './styled';

const { Flex, Avatar, Text } = Common;

interface ComposeConversationProps {
  onSubmit: (val: any) => any;
  onCancel: () => any;
  repliedComment?: any;
  isLoading?: boolean;
  avatar?: string;
  username?: string;
}

function ComposeConversation({
  onSubmit,
  onCancel,
  repliedComment = null,
  isLoading = false,
  avatar = '',
  username = 'stockbit',
}: ComposeConversationProps) {
  const [isExpand, setIsExpand] = useState(false);
  const [isReplyComment, setIsReplyComment] = useState(false);
  const [content, setContent] = useState('');
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState([]);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);

  const {
    dropzoneRef,
    handleOnDragOver,
    handleOnDragStart,
    handleOnDrop,
  } = useDropFile({
    isAllowedToUpload: isExpand,
    onErrorMaxSize: () => { },
    setDocuments,
    setImages,
    images,
    documents,
  });

  useEffect(() => {
    setIsReplyComment(!!repliedComment);
    if (repliedComment) {
      // set initial content
      setContent(`@${repliedComment.username} `);

      setIsExpand(true);
    }
  }, [repliedComment]);

  const handleReset = () => {
    setContent('');
    setDocuments([]);
    setImages([]);
    setIsReplyComment(false);
    setIsExpand(false);
    setIsFooterExpanded(false);
  };

  const handleChangeContent = (value) => {
    setContent(value);
  };

  const handleSelectEmoji = (emoji) => {
    setContent(`${content} ${emoji} `);
  };

  const handleToggleExpand = () => {
    setIsExpand(!isExpand);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validate content
    if (!content) {
      alertMessage({
        content: ERROR_MESSAGE.STREAM_TEXT_REQUIRED,
        alertType: 'snackbar',
        messageType: 'error',
        hasCloseIcon: true,
      });

      return;
    }

    const data: any = {
      content,
      image: images,
      file: documents,
    };

    if (isReplyComment) {
      data.replyto = repliedComment.postid;
    }

    const submit = await onSubmit(data);
    if (submit) {
      handleReset();
    }
  };

  const handleCancel = () => {
    handleReset();
    setIsExpand(false);
    onCancel();
  };

  const handleResetImage = () => {
    setImages([]);
  };

  const handleCloseNestedReply = () => {
    setIsReplyComment(false);
    onCancel();
  };

  const avatarUrl = `${AVATAR_URL}/${avatar}`;

  return (
    <ReplyCompose isExpand={isExpand}>
      <div
        onDragEnter={handleOnDragStart}
        onDragOver={handleOnDragOver}
        onDrop={handleOnDrop}
        ref={dropzoneRef}
      >
        <form onSubmit={handleSubmit}>
          <Flex align="flex-start" padding={isExpand ? '20px 20px 0 20px' : '20px'}>
            <Avatar size="small" padding="0px">
              <Image
                alt="imageprofile"
                src={avatarUrl}
                onError={(e) => handleErrorAvatar(e, username)}
              />
            </Avatar>
            {isExpand ? (
              <ComposeContent isExpand={isExpand} isFooterExpanded={isFooterExpanded}>
                <Textarea
                  placeholder="Tulis komen kamu disini..."
                  value={content}
                  onChange={handleChangeContent}
                />
                {images.length > 0 && (
                  <ImageItem
                    image={images[0]?.imagePreviewUrl}
                    imageDimensions={images[0]?.dimensions}
                    onDelete={handleResetImage}
                  />
                )}
                <Flex mb="10px">
                  <FileContainer
                    documents={documents}
                    onSetDocuments={setDocuments}
                  />
                </Flex>

                {isReplyComment && repliedComment && (
                  <NestedReplyBox
                    content={repliedComment.content_original || repliedComment.title}
                    username={repliedComment.username}
                    images={repliedComment.images}
                    closable
                    onClose={handleCloseNestedReply}
                  />
                )}
              </ComposeContent>
            ) : (
              <Text
                mt="12px"
                ml="10px"
                color={color.primary}
                onClick={handleToggleExpand}
              >
                Tulis komen kamu disini...
              </Text>
            )}
          </Flex>
          {isExpand && (
            <Footer
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              onSelectEmoji={handleSelectEmoji}
              onSelectGif={setImages}
              isLoading={isLoading}
              onSetDocuments={setDocuments}
              onSetImages={setImages}
              documents={documents}
              onExpandFooter={(val) => setIsFooterExpanded(val)}
            />
          )}
        </form>
      </div>
    </ReplyCompose>
  );
}

export default ComposeConversation;
